import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX, SkipBack, SkipForward } from 'lucide-react';

export default function MusicPage() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);

  const song = {
    title: "Tony's Luck",
    artist: "Twig Capone",
    audioUrl: "https://customer-assets.emergentagent.com/job_indie-label-hub-2/artifacts/ay8fv7d5_tony%E2%80%99s-luck.mp3"
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('ended', handleEnded);
      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('ended', handleEnded);
      };
    }
  }, []);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (e) => {
    const seekTime = (e.target.value / 100) * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = e.target.value / 100;
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="min-h-screen py-32 bg-[#1A1412]" data-testid="music-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-[#CFB53B] uppercase tracking-[0.2em] text-xs font-['Outfit'] font-light">Experience The Sound</span>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#FDFBF7] mb-4 tracking-tight mt-4">
            Music
          </h1>
          <p className="text-[#A89B95] text-lg font-['Outfit'] max-w-2xl mx-auto">
            Listen to the authentic sound of Vintage Ave Records
          </p>
        </motion.div>

        {/* Audio Player Section - Twig's Music First */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-16"
        >
          <div className="bg-gradient-to-br from-[#2C221F] to-[#1A1412] rounded-2xl p-8 border border-[#3D302C] shadow-xl">
            {/* Song Info */}
            <div className="flex items-center gap-6 mb-8">
              {/* Vinyl Animation */}
              <div className={`w-24 h-24 rounded-full bg-gradient-to-br from-[#1A1412] to-[#2C221F] border-4 border-[#D97736] flex items-center justify-center relative ${isPlaying ? 'animate-spin' : ''}`} style={{ animationDuration: '3s' }}>
                <div className="absolute inset-3 rounded-full bg-gradient-to-br from-[#D97736] to-[#CFB53B] opacity-20"></div>
                <div className="w-8 h-8 rounded-full bg-[#CFB53B] flex items-center justify-center">
                  <div className="w-3 h-3 rounded-full bg-[#1A1412]"></div>
                </div>
                <div className="absolute inset-6 rounded-full border border-[#3D302C]"></div>
                <div className="absolute inset-8 rounded-full border border-[#3D302C]"></div>
              </div>
              
              <div className="flex-1">
                <h2 className="font-['Playfair_Display'] text-3xl font-bold text-[#FDFBF7] mb-1">
                  {song.title}
                </h2>
                <p className="text-[#D97736] text-lg font-['Outfit']">
                  {song.artist}
                </p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-6">
              <input
                type="range"
                min="0"
                max="100"
                value={progress}
                onChange={handleSeek}
                className="w-full h-2 bg-[#3D302C] rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-[#D97736] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #D97736 0%, #D97736 ${progress}%, #3D302C ${progress}%, #3D302C 100%)`
                }}
              />
              <div className="flex justify-between text-sm text-[#A89B95] mt-2 font-['Outfit']">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-6">
              <button 
                onClick={() => {
                  if (audioRef.current) {
                    audioRef.current.currentTime = Math.max(0, currentTime - 10);
                  }
                }}
                className="p-3 text-[#A89B95] hover:text-[#FDFBF7] transition-colors"
              >
                <SkipBack size={24} />
              </button>
              
              <button
                onClick={togglePlay}
                className="w-16 h-16 bg-gradient-to-br from-[#D97736] to-[#F08F4D] rounded-full flex items-center justify-center text-white hover:shadow-[0_0_30px_rgba(217,119,54,0.5)] transition-all"
              >
                {isPlaying ? <Pause size={28} /> : <Play size={28} className="ml-1" />}
              </button>
              
              <button 
                onClick={() => {
                  if (audioRef.current) {
                    audioRef.current.currentTime = Math.min(duration, currentTime + 10);
                  }
                }}
                className="p-3 text-[#A89B95] hover:text-[#FDFBF7] transition-colors"
              >
                <SkipForward size={24} />
              </button>
            </div>

            {/* Volume Control */}
            <div className="flex items-center justify-center gap-3 mt-6">
              <button onClick={toggleMute} className="text-[#A89B95] hover:text-[#FDFBF7]">
                {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
              </button>
              <input
                type="range"
                min="0"
                max="100"
                value={isMuted ? 0 : volume * 100}
                onChange={handleVolumeChange}
                className="w-24 h-1 bg-[#3D302C] rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-[#CFB53B] [&::-webkit-slider-thumb]:rounded-full"
              />
            </div>

            <audio ref={audioRef} src={song.audioUrl} preload="metadata" />
          </div>
        </motion.div>

        {/* Coming Soon Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center gap-4">
            <div className="w-20 h-px bg-gradient-to-r from-transparent to-[#D97736]"></div>
            <span className="text-[#CFB53B] text-sm font-['Outfit'] uppercase tracking-widest">More Music Coming Soon</span>
            <div className="w-20 h-px bg-gradient-to-l from-transparent to-[#D97736]"></div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
